import { useEffect, useRef } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { cn } from '../lib/utils'
import { Icon, type IconName } from './ui/icons'
import { initialsFromName } from '../views/shared'

interface SidebarProps {
  isOpen?: boolean
  onClose?: () => void
  districtSummary?: string
  districtStatus?: string
  displayName?: string
  mode?: string
  pendingCTACount?: number
  unreadNotificationCount?: number
  onLogout?: () => void
}

type NavItem = { id: string; path: string; label: string; icon: IconName }
type NavSection = { section: string; items: NavItem[] }

const navSections: NavSection[] = [
  {
    section: 'Main',
    items: [
      { id: 'dashboard', path: '/', label: 'Home', icon: 'dashboard' },
      { id: 'actions', path: '/actions', label: 'Actions', icon: 'actions' },
      { id: 'notifications', path: '/notifications', label: 'Notifications', icon: 'notifications' },
      { id: 'topics', path: '/topics', label: 'Topics', icon: 'topics' },
    ],
  },
  {
    section: 'Civic Data',
    items: [
      { id: 'representatives', path: '/representatives?mine=true', label: 'Representatives', icon: 'representatives' },
      { id: 'bills', path: '/bills', label: 'Bills', icon: 'bills' },
    ],
  },
  {
    section: 'Explore civic activity',
    items: [
      { id: 'calendar', path: '/calendar', label: 'Calendar', icon: 'calendar' },
      { id: 'committees', path: '/committees', label: 'Committees', icon: 'committees' },
      { id: 'research', path: '/research', label: 'Research & Reports', icon: 'research' },
      { id: 'votes', path: '/votes', label: 'Votes', icon: 'bills' },
      { id: 'nominations', path: '/nominations', label: 'Nominations', icon: 'nominations' },
      { id: 'badges', path: '/badges', label: 'Badges', icon: 'badges' },
    ],
  },
  {
    section: 'Tools',
    items: [
      { id: 'settings', path: '/settings', label: 'Settings', icon: 'settings' },
    ],
  },
]

export default function Sidebar({
  isOpen = false,
  onClose,
  districtSummary,
  districtStatus,
  displayName,
  mode,
  pendingCTACount,
  unreadNotificationCount,
  onLogout,
}: SidebarProps) {
  const rootRef = useRef<HTMLDivElement | null>(null)
  const isMulti = mode === 'multi'

  // In multi-user mode, hide Settings (admin-only concerns)
  const filteredSections = isMulti
    ? navSections.map((section) => ({
        ...section,
        items: section.items.filter((item) => item.id !== 'settings'),
      })).filter((section) => section.items.length > 0)
    : navSections

  // Drawer a11y: when opened below the lg breakpoint, focus in, trap Tab,
  // handle Escape, restore focus on close. Above lg the sidebar is persistent
  // and `isOpen` is always false because the toggle button is hidden.
  useEffect(() => {
    if (!isOpen) return
    const root = rootRef.current
    if (!root) return

    const previouslyFocused = document.activeElement as HTMLElement | null
    const focusables = () => Array.from(
      root.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'),
    )
    const initial = focusables()
    initial[0]?.focus()

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose?.()
        return
      }
      if (event.key !== 'Tab') return
      const items = focusables()
      if (items.length === 0) return
      const first = items[0]
      const last = items[items.length - 1]
      const active = document.activeElement as HTMLElement | null
      const insideRoot = root.contains(active)
      if (event.shiftKey) {
        if (!insideRoot || active === first) {
          event.preventDefault()
          last.focus()
        }
      } else if (!insideRoot || active === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('keydown', handleKey)
      // Restore focus to the trigger that opened the drawer. If that
      // trigger no longer exists in the DOM (rare after a route change),
      // blur whatever is currently focused so we don't strand focus on a
      // hidden drawer node.
      if (previouslyFocused && document.body.contains(previouslyFocused)) {
        previouslyFocused.focus()
      } else {
        ;(document.activeElement as HTMLElement | null)?.blur?.()
      }
    }
  }, [isOpen, onClose])

  return (
    <div
      ref={rootRef}
      role={isOpen ? 'dialog' : undefined}
      aria-modal={isOpen ? true : undefined}
      aria-label={isOpen ? 'Main navigation' : undefined}
      className={cn(
        // Drawer below lg, persistent sidebar from lg+. Width drops from 288 → 240.
        'fixed inset-y-0 left-0 z-50 w-72 border-r border-slate-200 bg-white text-slate-900',
        'transform transition-transform duration-200 ease-in-out',
        isOpen ? 'visible translate-x-0' : 'invisible -translate-x-full',
        'lg:visible lg:translate-x-0 lg:w-60 lg:transition-none',
      )}
    >
      <div className="flex h-full flex-col">
        {/* Logo */}
        <div className="flex h-14 items-center justify-between px-4 border-b border-slate-200">
          <Link
            to="/"
            className="flex items-center gap-2 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            onClick={() => onClose?.()}
          >
            <span aria-hidden="true" className="flex h-8 w-8 items-center justify-center rounded-md bg-blue-600 text-sm font-bold text-white">
              C
            </span>
            <span className="text-sm font-semibold text-slate-900">Constituent</span>
          </Link>
          <button
            type="button"
            aria-label="Close navigation"
            onClick={onClose}
            className="rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 lg:hidden"
          >
            <Icon name="close" className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav aria-label="Primary" className="sidebar-scroll flex-1 overflow-y-auto px-2 py-3">
          {filteredSections.map((section, sectionIdx) => (
            <div key={section.section} className={sectionIdx === 0 ? '' : 'mt-4'}>
              <h3 className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                {section.section}
              </h3>
              {section.items.map((tab) => (
                <NavLink
                  key={tab.id}
                  to={tab.path}
                  end={tab.path === '/'}
                  onClick={() => onClose?.()}
                  className={({ isActive }) => cn(
                    'group mt-0.5 flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
                    isActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900',
                  )}
                >
                  {({ isActive }) => (
                    <>
                      <Icon
                        name={tab.icon}
                        className={cn(
                          'h-5 w-5 shrink-0',
                          isActive ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-600',
                        )}
                      />
                      <span className="truncate">{tab.label}</span>
                      {tab.id === 'actions' && !!pendingCTACount && pendingCTACount > 0 ? (
                        <span aria-label={`${pendingCTACount} pending`} className="ml-auto inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-blue-600 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white">
                          {pendingCTACount > 99 ? '99+' : pendingCTACount}
                        </span>
                      ) : tab.id === 'notifications' && !!unreadNotificationCount && unreadNotificationCount > 0 ? (
                        <span aria-label={`${unreadNotificationCount} unread`} className="ml-auto inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-rose-600 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white">
                          {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                        </span>
                      ) : null}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* Footer — clicking anywhere navigates to Profile */}
        <div className="border-t border-slate-200 p-3">
          <Link
            to="/profile"
            onClick={() => onClose?.()}
            className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-2.5 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <span aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
              {initialsFromName(displayName)}
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-medium text-slate-900">{displayName || 'Not signed in'}</span>
              <span className="truncate text-xs text-slate-500">
                {districtSummary ?? 'Address not set'}
              </span>
            </span>
          </Link>
          {districtStatus && districtStatus !== 'resolved' && (
            <p className="mt-1 px-1 text-[11px] text-amber-700">
              Address lookup: {districtStatus}
            </p>
          )}
          {isMulti && onLogout && (
            <button
              type="button"
              onClick={() => { onClose?.(); onLogout() }}
              className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm font-semibold text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              Sign Out
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
